// src/firebase/firebaseConfig.js
import { initializeApp, getApps, getApp } from "firebase/app";
import { initializeAppCheck, ReCaptchaV3Provider } from "firebase/app-check";
import {
  getAuth,
  signInAnonymously,
  setPersistence,
  indexedDBLocalPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  inMemoryPersistence,
} from "firebase/auth";
import { getFirestore } from "firebase/firestore";
import { getStorage } from "firebase/storage";
import { getFunctions } from "firebase/functions";

/**
 * Firebase config from CRA env vars.
 * Required:
 *  - REACT_APP_FIREBASE_API_KEY
 *  - REACT_APP_FIREBASE_AUTH_DOMAIN
 *  - REACT_APP_FIREBASE_PROJECT_ID
 *  - REACT_APP_FIREBASE_STORAGE_BUCKET
 *  - REACT_APP_FIREBASE_MESSAGING_SENDER_ID
 *  - REACT_APP_FIREBASE_APP_ID
 * Optional:
 *  - REACT_APP_APPCHECK_SITE_KEY (reCAPTCHA v3 site key, for App Check)
 */
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

// Initialize Firebase only once
export const app = getApps().length ? getApp() : initializeApp(firebaseConfig);

// Services
export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);
export const functions = getFunctions(app, "us-central1");

/**
 * Init App Check.
 * NOTE: For local dev, set a debug token BEFORE this runs:
 *   - in src/index.js (recommended), or
 *   - self.FIREBASE_APPCHECK_DEBUG_TOKEN = "<uuid-v4>"
 */
let _appCheckInited = false;
export function initAppCheck() {
  if (_appCheckInited) return;

  const siteKey = process.env.REACT_APP_APPCHECK_SITE_KEY;
  if (!siteKey) {
    _appCheckInited = true;
    return;
  }

  try {
    const tokenFromWindow =
      (typeof window !== "undefined" && window.FIREBASE_APPCHECK_DEBUG_TOKEN) ||
      null;

    const tokenFromLocalStorage =
      (typeof window !== "undefined" &&
        window.localStorage &&
        window.localStorage.getItem("firebase-app-check-debug-token")) ||
      null;

    const dbg = tokenFromWindow || tokenFromLocalStorage;
    if (dbg) {
      self.FIREBASE_APPCHECK_DEBUG_TOKEN = dbg;
    }
  } catch (_) {}

  initializeAppCheck(app, {
    provider: new ReCaptchaV3Provider(siteKey),
    isTokenAutoRefreshEnabled: true,
  });

  _appCheckInited = true;
}

export async function ensureDurableAuthPersistence() {
  // IPAD-10:
  // 1) IndexedDB is preferred for installed PWAs.
  // 2) localStorage persistence is the normal browser fallback.
  // 3) Safari Private Browsing can reject both persistent stores while
  //    sessionStorage still works. browserSessionPersistence survives the
  //    post-link page reload, unlike inMemoryPersistence.
  // 4) in-memory is the absolute last resort only.
  const candidates = [
    indexedDBLocalPersistence,
    browserLocalPersistence,
    browserSessionPersistence,
    inMemoryPersistence,
  ];

  for (const persistence of candidates) {
    try {
      await setPersistence(auth, persistence);
      return persistence;
    } catch {
      // try the next supported persistence layer
    }
  }

  return null;
}

/**
 * ✅ Staff uses anonymous auth. This MUST actually sign in (mobile often has no session yet).
 */
export async function ensureAnonymousAuth() {
  // Already signed in
  if (auth.currentUser) return auth.currentUser;

  await ensureDurableAuthPersistence();

  // Sign in anonymously (creates request.auth.uid for Storage rules)
  const cred = await signInAnonymously(auth);
  return cred.user;
}

// ✅ IMPORTANT: include functions in default export
export default {
  app,
  auth,
  db,
  storage,
  functions,
  initAppCheck,
  ensureAnonymousAuth,
  ensureDurableAuthPersistence,
};
